import React, { useState, useEffect, useCallback } from "react";
import { RotateCcw, Trash2, Calendar, User, Eye, AlertCircle } from "lucide-react";
import { trashService, DeletedUser } from "../../services/trashService";
import { formatDateTime } from "../../utils/dateFormatter";
import TrashDetailModal from "../TrashDetailModal";
import "../../styles/components/deleted-users.css";

const DeletedUsers: React.FC = () => {
  const [users, setUsers] = useState<DeletedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedUser, setSelectedUser] = useState<DeletedUser | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trashService.getDeletedUsers(page, limit);
      setUsers(response.data);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleRestore = async (userId: number) => {
    try {
      await trashService.restoreUser(userId);
      setUsers(users.filter(user => user.id !== userId));
      setSelectedUsers(prev => prev.filter(id => id !== userId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục người dùng');
    }
  };

  const handlePermanentDelete = async (userId: number) => {
    try {
      await trashService.permanentDeleteUser(userId);
      setUsers(users.filter(user => user.id !== userId));
      setSelectedUsers(prev => prev.filter(id => id !== userId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn người dùng');
    }
  };

  const handleBulkRestore = async () => {
    try {
      await trashService.bulkRestoreUsers(selectedUsers);
      setUsers(users.filter(user => !selectedUsers.includes(user.id)));
      setSelectedUsers([]);
      setTotal(prev => prev - selectedUsers.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục các người dùng');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await trashService.bulkDeleteUsers(selectedUsers);
      setUsers(users.filter(user => !selectedUsers.includes(user.id)));
      setSelectedUsers([]);
      setTotal(prev => prev - selectedUsers.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn các người dùng');
    }
  };

  const toggleSelectUser = (userId: number) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    setSelectedUsers(users.map(user => user.id));
  };

  const deselectAll = () => {
    setSelectedUsers([]);
  };

  const handleViewDetails = (user: DeletedUser) => {
    setSelectedUser(user);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedUser(null);
  };

  const getRoleLabel = (role: string) => {
    const roleLabels: { [key: string]: string } = {
      'MEMBER': 'Thành viên',
      'PREMIUM': 'Thành viên Premium',
      'TOOL_USER': 'Người dùng công cụ',
      'MODERATOR': 'Kiểm duyệt viên',
      'ADMIN': 'Quản trị viên'
    };
    return roleLabels[role] || role;
  };

  if (loading) {
    return (
      <div className="trash-loading">
        <div className="trash-loading__spinner"></div>
        <p>Đang tải danh sách người dùng đã xóa...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trash-empty">
        <AlertCircle size={48} className="trash-empty__icon" />
        <h3 className="trash-empty__title">Lỗi tải dữ liệu</h3>
        <p className="trash-empty__description">{error}</p>
        <button onClick={loadUsers} className="trash-empty__retry">
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="deleted-users">
      <div className="deleted-users__header">
        <div className="deleted-users__info">
          <h3 className="deleted-users__title">Người dùng đã xóa ({users.length})</h3>
          <p className="deleted-users__description">Danh sách các người dùng đã bị xóa và có thể khôi phục</p>
        </div>
        
        {users.length > 0 && (
          <div className="deleted-users__bulk-actions">
            <button 
              className="deleted-users__bulk-btn deleted-users__bulk-btn--restore"
              onClick={handleBulkRestore}
              disabled={selectedUsers.length === 0}
            >
              <RotateCcw size={16} />
              Khôi phục ({selectedUsers.length})
            </button>
            <button 
              className="deleted-users__bulk-btn deleted-users__bulk-btn--delete"
              onClick={handleBulkDelete}
              disabled={selectedUsers.length === 0}
            >
              <Trash2 size={16} />
              Xóa vĩnh viễn ({selectedUsers.length})
            </button>
            <button 
              className="deleted-users__bulk-btn deleted-users__bulk-btn--select"
              onClick={selectedUsers.length === users.length ? deselectAll : selectAll}
            >
              {selectedUsers.length === users.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
            </button>
          </div>
        )}
      </div>

      {users.length === 0 ? (
        <div className="trash-empty">
          <Trash2 size={48} className="trash-empty__icon" />
          <h3 className="trash-empty__title">Không có người dùng nào trong thùng rác</h3>
          <p className="trash-empty__description">Tất cả người dùng đều đang hoạt động bình thường</p>
        </div>
      ) : (
        <div className="deleted-users__list">
          {users.map((user) => (
            <div key={user.id} className="deleted-users__item">
              <div className="deleted-users__checkbox">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => toggleSelectUser(user.id)}
                />
              </div>
              
              <div className="deleted-users__content">
                <div className="deleted-users__item-header">
                  <h4 className="deleted-users__item-name">{user.fullName}</h4>
                  <span className="deleted-users__item-status deleted-users__item-status--deleted">Đã xóa</span>
                </div>
                
                <div className="deleted-users__meta">
                  <div className="deleted-users__meta-item">
                    <User size={14} />
                    <span>Username: {user.username}</span>
                  </div>
                  <div className="deleted-users__meta-item">
                    <span>Email: {user.email}</span>
                  </div>
                  <div className="deleted-users__meta-item">
                    <span>Vai trò: {getRoleLabel(user.role)}</span>
                  </div>
                  <div className="deleted-users__meta-item">
                    <Calendar size={14} />
                    <span>Xóa lúc: {formatDateTime(user.deletedAt)}</span>
                  </div>
                  <div className="deleted-users__meta-item">
                    <span>Xóa bởi: {user.deletedBy}</span>
                  </div>
                </div>
              </div>
              
              <div className="deleted-users__actions">
                <button 
                  className="deleted-users__action-btn deleted-users__action-btn--preview"
                  onClick={() => handleViewDetails(user)}
                  title="Xem chi tiết"
                >
                  <Eye size={16} />
                </button>
                <button 
                  className="deleted-users__action-btn deleted-users__action-btn--restore"
                  onClick={() => handleRestore(user.id)}
                  title="Khôi phục"
                >
                  <RotateCcw size={16} />
                </button>
                <button 
                  className="deleted-users__action-btn deleted-users__action-btn--delete"
                  onClick={() => handlePermanentDelete(user.id)}
                  title="Xóa vĩnh viễn"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {total > limit && (
        <div className="deleted-users__pagination">
          <button 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="deleted-users__pagination-btn"
          >
            Trước
          </button>
          <span className="deleted-users__pagination-info">
            Trang {page} / {Math.ceil(total / limit)}
          </span>
          <button 
            onClick={() => setPage(prev => prev + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="deleted-users__pagination-btn"
          >
            Sau
          </button>
        </div>
      )}
      
      {/* Detail Modal */}
      <TrashDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        item={selectedUser}
        type="user"
      />
    </div>
  );
};

export default DeletedUsers;
